// components/QRCodeScanner.jsx
import React, { useEffect, useRef, useState } from 'react'
// Décodeur WASM (zxing-wasm) : fiable sur iOS Safari (≠ zxing-js de html5-qrcode).
// Import « pure » → on utilise toujours notre WASM, jamais le BarcodeDetector natif.
import { BarcodeDetector, setZXingModuleOverrides } from 'barcode-detector/pure'
// Par défaut zxing-wasm va chercher le .wasm sur un CDN (jsdelivr), bloqué par la
// CSP du backoffice. On le sert depuis notre propre domaine via Vite (?url).
import zxingWasmUrl from 'zxing-wasm/reader/zxing_reader.wasm?url'

setZXingModuleOverrides({
    locateFile: (path, prefix) =>
        path.endsWith('.wasm') ? zxingWasmUrl : prefix + path,
})

const STORAGE_KEY = 'qr-scanner-camera-id'
const FORMATS = ['qr_code', 'code_128', 'code_39', 'itf', 'codabar']
const DETECT_INTERVAL_MS = 100 // ~10 décodages/seconde

const isBackCamera = (label = '') => /back|rear|arri|environment/i.test(label)
const isFrontCamera = (label = '') => /front|user|face|avant/i.test(label)

const QRCodeScanner = ({ onScanSuccess, onScanFailure }) => {
    const videoRef = useRef(null)
    const streamRef = useRef(null)
    const loopRef = useRef(null)
    const detectorRef = useRef(null)

    const onScanSuccessRef = useRef(onScanSuccess)
    const onScanFailureRef = useRef(onScanFailure)
    onScanSuccessRef.current = onScanSuccess
    onScanFailureRef.current = onScanFailure

    const [cameras, setCameras] = useState([])
    const [cameraId, setCameraId] = useState(null)

    const stopStream = () => {
        if (loopRef.current) {
            clearInterval(loopRef.current)
            loopRef.current = null
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop())
            streamRef.current = null
        }
    }

    // Permission + énumération des caméras, puis choix par défaut
    // (mémorisé > frontale > 1ère). Les labels ne sont dispos qu'après permission.
    useEffect(() => {
        let cancelled = false
        const init = async () => {
            try {
                const tmp = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'user' },
                })
                tmp.getTracks().forEach((t) => t.stop())
                const devices = (
                    await navigator.mediaDevices.enumerateDevices()
                ).filter((d) => d.kind === 'videoinput')
                if (cancelled) return
                setCameras(devices)
                const stored = localStorage.getItem(STORAGE_KEY)
                const storedExists = devices.some((d) => d.deviceId === stored)
                const front = devices.find((d) => isFrontCamera(d.label))
                setCameraId(
                    storedExists
                        ? stored
                        : front?.deviceId ?? devices[0]?.deviceId ?? null
                )
            } catch (error) {
                console.error('[QR] permission/énumération caméra échouée:', error)
            }
        }
        init()
        return () => {
            cancelled = true
        }
    }, [])

    // (Re)démarre le flux + la boucle de détection à chaque changement de caméra
    useEffect(() => {
        if (!cameraId) return
        let cancelled = false

        const start = async () => {
            stopStream()
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { deviceId: { exact: cameraId } },
                })
                if (cancelled) {
                    stream.getTracks().forEach((t) => t.stop())
                    return
                }
                streamRef.current = stream

                const video = videoRef.current
                video.srcObject = stream
                video.setAttribute('playsinline', 'true') // iOS : pas de plein écran
                await video.play()
                console.log(
                    '[QR] caméra démarrée',
                    cameraId,
                    `${video.videoWidth}x${video.videoHeight}`
                )

                if (!detectorRef.current) {
                    detectorRef.current = new BarcodeDetector({ formats: FORMATS })
                }

                let busy = false
                loopRef.current = setInterval(async () => {
                    if (cancelled || busy || video.readyState < 2) return
                    busy = true
                    try {
                        const codes = await detectorRef.current.detect(video)
                        if (codes?.[0]?.rawValue) {
                            console.log('[QR] décodé:', codes[0].rawValue)
                            onScanSuccessRef.current(codes[0].rawValue, codes[0])
                        }
                    } catch (error) {
                        onScanFailureRef.current?.(error)
                    } finally {
                        busy = false
                    }
                }, DETECT_INTERVAL_MS)
            } catch (error) {
                console.error('[QR] démarrage caméra échoué:', error)
            }
        }
        start()

        return () => {
            cancelled = true
            stopStream()
        }
    }, [cameraId])

    const handleCameraChange = (e) => {
        const id = e.target.value
        localStorage.setItem(STORAGE_KEY, id)
        setCameraId(id)
    }

    const selected = cameras.find((c) => c.deviceId === cameraId)
    // Miroir « selfie » pour la frontale, pas de miroir pour l'arrière.
    // (N'affecte pas le décodage : detect() lit la frame réelle, pas le rendu CSS.)
    const mirrored = selected ? !isBackCamera(selected.label) : true

    return (
        <div className="w-full bg-gray-100">
            {cameras.length > 1 && (
                <div className="p-2">
                    <select
                        value={cameraId ?? ''}
                        onChange={handleCameraChange}
                        className="w-full rounded-sm border border-gray-300 px-2 py-1 text-sm"
                    >
                        {cameras.map((cam, i) => (
                            <option key={cam.deviceId} value={cam.deviceId}>
                                {cam.label || `Caméra ${i + 1}`}
                            </option>
                        ))}
                    </select>
                </div>
            )}
            <video
                ref={videoRef}
                muted
                playsInline
                className="w-full bg-gray-200"
                style={{
                    aspectRatio: '16/9',
                    objectFit: 'cover',
                    transform: mirrored ? 'scaleX(-1)' : 'none',
                }}
            />
            <p className="text-center text-sm text-gray-500 py-2">
                Toute la zone détecte les QR codes et codes-barres
            </p>
        </div>
    )
}

export default QRCodeScanner
